import React from 'react';
import { Routes, Route, Navigate, Outlet, useLocation, useParams } from 'react-router-dom';
import withAlert from '../../components/withAlert';

// Importando os componentes/páginas
import { Dashboard } from '../dashboard/Dashboard';
import { Downloads } from '../dashboard/Downloads';
import { Prints } from '../dashboard/Prints';
import { NoLink } from '../dashboard/NoLink';
import { Filed } from '../document/Filed';
import DocumentDetailsRead from '../document/DocumentDetailsRead';
import VersionDiff from '../document/VersionDiff';
import Document from '../document/Document';
import DocumentCopy from '../document/DocumentCopy';
import DocumentDetails from '../document/DocumentDetails';
import DocumentEdit from '../document/DocumentEdit';
import Loading from '../document/Loading';
import { DocumentEditor } from '../document/DocumentEditor';
import DocumentPrint from '../document/DocumentPrint';
import { DocumentInteraction } from '../document/DocumentInteraction';
import DocumentMove from '../document/DocumentMove';
import DocumentPublish from '../document/DocumentPublish';
import DocumentReview from '../document/DocumentReview';
import NewVersion from '../document/NewVersion';
import { VersionHistory } from '../document/VersionHistory';
import { TrashBin } from '../document/TrashBin';
import { PermissionGroup } from '../permissiongroup/PermissionGroup';
import { PermissionGroupIndex } from '../permissiongroup/PermissionGroupIndex';
import Notification from '../notification/Notification';
import FolderIndex from '../folder/FolderIndex';
import { DocumentReading } from '../report/DocumentReading';
import User from '../user/User';
import InviteUsers from '../user/InviteUsers';
import UserIndex from '../user/UserIndex';
import { Backup } from './Backup';
import { Extract } from '../financial/Extract';
import NotFound from '../default-v2/NotFound';
import Index from './Index';
import NormIndex from '../norm/NormIndex';
import NormInteractions from '../norm/NormInteractions';
import RequisiteIndex from '../norm/RequisiteIndex';
import RequisiteShow from '../norm/RequisiteShow';
import ChangeEmail from '../login/ChangeEmail';
import PendingNormsList from '../norm/PendingNormsList';
import RegisterActivation from '../login/RegisterActivation';
import QualityPanelIndex from '../quality-panel/QualityPanelIndex';
import QualityPanelConsultant from '../quality-panel/QualityPanelConsultant';
import AuditIndex from '../audit/AuditIndex';
import AuditInteractions from '../audit/AuditInteractions';
import DashboardAudit from '../audit/DashboardAudit';
import ConfigHeaderFooter from '../document/ConfigHeaderFooter';
import Customer from '../customer/Customer';
import AdhesionContract from '../myaccount/AdhesionContract';
import Pdf from '../report/Pdf';
import General from '../myaccount/General';
import MonitoringNotificationPreferencesPage from '../myaccount/MonitoringNotificationPreferencesPage';
import ReadingDocumentDelegates from '../report/ReadingDocumentDelegates';
import ReadingDocumentResponsible from '../report/ReadingDocumentResponsible';
import EditsDocumentDelegates from '../report/EditsDocumentDelegates';
import EditsDocumentResponsible from '../report/EditsDocumentResponsible';
import Request from '../report/Request';
import PendingPerUser from '../report/PendingPerUser';
import ExtractHistory from '../financial/ExtractHistory';
import MasterList from '../report/MasterList';
import ActivitiesPending from '../report/ActivitiesPending';
import MigrateResponsibilityDocument from '../document/MigrateResponsibilityDocument';
import MigrateResponsibilityQuality from '../quality-panel/MigrateResponsibilityQuality';
import MigrateResponsibilityOperational from '../operational-panel/MigrateResponsibilityOperational';
import MigrateResponsibilityAudits from '../audit/MigrateResponsibilityAudit';
import MigrateResponsibilityMonitoring from '../monitoring/MigrateResponsibilityMonitoring';
import DocumentAccess from '../document/DocumentAccess';
import withRouter from '../../config/WithRouter';
import { fnDecodeStorageToken } from '../../utils/encodeLocalStorage';

const aTruthyValues = [true, 1, '1', 'true'];

function fnIsDocumentsV2Enabled() {
  const oToken = fnDecodeStorageToken();

  if (!oToken) return false;
  if (aTruthyValues.includes(oToken.bTrial)) return true;

  const nDocumentsVersion = Number(oToken.aModulesVersion?.documents?.version || 1);

  return nDocumentsVersion >= 2;
}

/**
 * Monta a rota equivalente no Documents V2 sem perder o contexto da URL.
 *
 * @param {string} cAction - view, editor, review ou publish.
 * @param {string} nVersionId - ID da versão do documento.
 * @param {Location} oLocation - Localização atual, incluindo query e hash.
 * @returns {string} Rota do frontend novo com query/hash preservados.
 */
const fnGetDocumentsV2Path = (cAction, nVersionId, oLocation) => {
  const cTargetPath = cAction === 'view'
    ? `/folder/${nVersionId}/view`
    : `/folder/${cAction}/${nVersionId}`;
  const cSearch = oLocation?.search || '';
  const cHash = oLocation?.hash || '';

  return `${cTargetPath}${cSearch}${cHash}`;
};

const LegacyDocumentsRouteGuard = () => {
  const location = useLocation();
  const cNormalizedPath = location.pathname.replace(/\/$/, '');
  // Configure e lixeira ainda vivem só no legado; clientes com Documents v2
  // precisam acessá-las sem serem redirecionados para /home.
  const bIsDocumentConfigureRoute = cNormalizedPath === '/document/configure';
  const bIsTrashRoute = cNormalizedPath === '/trash' || cNormalizedPath.startsWith('/trash/');
  const oLegacyDocumentMatch = cNormalizedPath.match(/^\/document\/(detail|editor|review|publish)\/([^/]+)$/);
  const oLegacyFolderMatch = cNormalizedPath.match(/^\/folder_old(?:\/([^/]+))?$/);

  // Links antigos podem ser acessados pelo proxy no bundle legado. Em clientes
  // V2, o redirect precisa ocorrer aqui antes do guard legado mandar o usuário
  // para /home; /folder/... será encaminhado pelo proxy ao frontend novo.
  if (fnIsDocumentsV2Enabled() && oLegacyDocumentMatch) {
    const cLegacyAction = oLegacyDocumentMatch[1];
    const cDocumentsV2Action = cLegacyAction === 'detail' ? 'view' : cLegacyAction;
    window.location.replace(fnGetDocumentsV2Path(cDocumentsV2Action, oLegacyDocumentMatch[2], location));
    return null;
  }

  // Preserve a pasta indicada no link antigo. Sem este caso, clientes V2
  // caem em /home e perdem o destino originalmente salvo no documento.
  if (fnIsDocumentsV2Enabled() && oLegacyFolderMatch) {
    const oSearchParams = new URLSearchParams(location.search || '');
    const cFolderId = oLegacyFolderMatch[1];

    if (cFolderId) oSearchParams.set('folderId', cFolderId);

    const cSearch = oSearchParams.toString() ? `?${oSearchParams.toString()}` : '';
    window.location.replace(`/folder${cSearch}${location.hash || ''}`);
    return null;
  }

  if (fnIsDocumentsV2Enabled() && !bIsDocumentConfigureRoute && !bIsTrashRoute) {
    return <Navigate to="/home" replace />;
  }

  return <Outlet />;
};

const DocumentRedirectRoute = () => {
  const { cAction, nVersionId } = useParams();
  const location = useLocation();
  const cRedirectAction = ['editor', 'review', 'publish'].includes(cAction) ? cAction : 'view';

  if (fnIsDocumentsV2Enabled()) {
    window.location.assign(fnGetDocumentsV2Path(cRedirectAction, nVersionId, location));
    return null;
  }

  const cLegacyAction = cRedirectAction === 'view' ? 'detail' : cRedirectAction;

  return <Navigate to={`/document/${cLegacyAction}/${nVersionId}`} replace />;
};

// Layout para rotas protegidas (internas)
const AppLayout = () => {
  return (
    <div>
      {/* Se houver um layout comum (e.g. cabeçalho, sidebar, rodapé), insira-o aqui */}
      <Outlet />
    </div>
  );
};

// Layout para rotas públicas
const PublicLayout = () => {
  return (
    <div>
      <Outlet />
    </div>
  );
};

const RouterMain = () => {
  return (
    <Routes>
      {/* Rotas internas (usando AppLayout) */}
      <Route path="/" element={<AppLayout />}>
        <Route index element={React.createElement(withAlert(withRouter(Dashboard)))} />
        <Route path="home/:nSynchronization?" element={React.createElement(withAlert(withRouter(Dashboard)))} />
        <Route path="login" element={<Navigate to="/" replace />} />
        <Route path="reset-email/:cToken" element={<ChangeEmail />} />

        <Route path="user" element={React.createElement(withAlert(UserIndex))} />
        <Route path="user/update/:nId" element={React.createElement(withAlert(User))} />
        <Route path="invite-users" element={React.createElement(withAlert(InviteUsers))} />

        <Route path="notification" element={React.createElement(withAlert(Notification))} />
        <Route path="pending-norms" element={React.createElement(withAlert(PendingNormsList))} />

        <Route path="report">
          <Route path="request/update/:nRequestId" element={React.createElement(withAlert(withRouter(Request)))} />
          <Route path="request/accept/:nRequestId" element={React.createElement(withAlert(withRouter(Request)))} />
          <Route path="request/:nRequestId?" element={React.createElement(withAlert(withRouter(Request)))} />
          <Route path="activities-pending" element={React.createElement(withAlert(ActivitiesPending))} />
          <Route path="reading-document" element={React.createElement(withAlert(withRouter(DocumentReading)))} />
          <Route path="reading-document-delegate" element={React.createElement(withAlert(withRouter(ReadingDocumentDelegates)))} />
          <Route path="reading-document-responsible" element={React.createElement(withAlert(withRouter(ReadingDocumentResponsible)))} />
          <Route path="edits-document-delegate" element={React.createElement(withAlert(withRouter(EditsDocumentDelegates)))} />
          <Route path="edits-document-responsible" element={React.createElement(withAlert(EditsDocumentResponsible))} />
          <Route path="audit/:nAuditId?" element={React.createElement(withAlert(withRouter(Pdf)))} />
        </Route>

        <Route path="folder" element={<LegacyDocumentsRouteGuard />}>
          <Route index element={React.createElement(withAlert(withRouter(FolderIndex)))} />
          <Route path=":nFolderId" element={React.createElement(withAlert(withRouter(FolderIndex)))} />
        </Route>
        <Route path="folder_old" element={<LegacyDocumentsRouteGuard />}>
          <Route index element={React.createElement(withAlert(withRouter(FolderIndex)))} />
          <Route path=":nFolderId" element={React.createElement(withAlert(withRouter(FolderIndex)))} />
        </Route>

        <Route path="document/redirect/:nVersionId" element={<DocumentRedirectRoute />} />
        <Route path="document/redirect/:cAction/:nVersionId" element={<DocumentRedirectRoute />} />
        {/* A migração é administrativa e usa endpoints compatíveis com V1 e V2.
            Não deve passar pelo bloqueio das telas legadas de documentos. */}
        <Route path="document/migrate-responsibility/:nId" element={React.createElement(withAlert(MigrateResponsibilityDocument))} />

        <Route path="document" element={<LegacyDocumentsRouteGuard />}>
          <Route path="create/:nFolderId?" element={React.createElement(withAlert(withRouter(Document)))} />
          <Route path="loading" element={React.createElement(withAlert(Loading))} />
          <Route path="review/:nVersionId" element={React.createElement(withAlert(withRouter(DocumentReview)))} />
          <Route path="publish/:nVersionId" element={React.createElement(withAlert(withRouter(DocumentPublish)))} />
          <Route path="editor/:nVersionId" element={React.createElement(withAlert(withRouter(DocumentEditor)))} />
          <Route path="edit/:nId" element={React.createElement(withAlert(withRouter(DocumentEdit)))} />
          <Route path="move/:nId" element={React.createElement(withAlert(withRouter(DocumentMove)))} />
          <Route path="detail/:nVersionId" element={React.createElement(withAlert(withRouter(DocumentDetails)))} />
          <Route path="no-links" element={React.createElement(withAlert(NoLink))} />
          <Route path="reading/:nVersionId" element={React.createElement(withAlert(withRouter(DocumentDetailsRead)))} />
          <Route path="print/:nDocumentId" element={React.createElement(withAlert(withRouter(DocumentPrint)))} />
          <Route path="download" element={React.createElement(withAlert(withRouter(Downloads)))} />
          <Route path="print" element={React.createElement(withAlert(withRouter(Prints)))} />
          <Route path="copy/:nVersionId" element={React.createElement(withAlert(withRouter(DocumentCopy)))} />
          <Route path="history/:nId" element={React.createElement(withAlert(withRouter(VersionHistory)))} />
          <Route path="interaction/:nVersionId" element={React.createElement(withAlert(withRouter(DocumentInteraction)))} />
          <Route path="compare/:nId/:cV1/:cV2" element={React.createElement(withAlert(withRouter(VersionDiff)))} />
          <Route path="new-version/:nId" element={React.createElement(withAlert(NewVersion))} />
        </Route>

        <Route path="quality-panel" element={React.createElement(withAlert(withRouter(QualityPanelIndex)))} />
        <Route path="requisite-audit/:nAuditId?/:nEvidenceId?" element={React.createElement(withAlert(withRouter(AuditIndex)))} />
        <Route path="quality-panel/migrate-responsibility/:nId" element={React.createElement(withAlert(MigrateResponsibilityQuality))} />
        <Route path="audits/migrate-responsibility/:nId" element={React.createElement(withAlert(MigrateResponsibilityAudits))} />
        <Route path="monitoring/migrate-responsibility/:nId" element={React.createElement(withAlert(MigrateResponsibilityMonitoring))} />
        {/* Temporary alias until the public gateway routes this flow to the legacy frontend. */}
        <Route path="user/monitoring-responsibility/migrate/:nId" element={React.createElement(withAlert(MigrateResponsibilityMonitoring))} />
        <Route path="audit-management" element={React.createElement(withAlert(withRouter(DashboardAudit)))} />
        <Route path="audit/interaction/:nAuditId?" element={React.createElement(withAlert(AuditInteractions))} />

        <Route path="norm">
          <Route path=":nId?" element={React.createElement(withAlert(withRouter(NormIndex)))} />
          <Route path="interaction/:nId?" element={React.createElement(withAlert(NormInteractions))} />
        </Route>

        <Route path="requisite">
          <Route path=":nNormId?/:bDeadline?" element={React.createElement(withAlert(withRouter(RequisiteIndex)))} />
          <Route path="detail/:nId?" element={React.createElement(withAlert(withRouter(RequisiteShow)))} />
        </Route>

        <Route path="team">
          <Route index element={React.createElement(withAlert(withRouter(PermissionGroupIndex)))} />
          <Route path="create" element={React.createElement(withAlert(withRouter(PermissionGroup)))} />
          <Route path="update/:nId" element={React.createElement(withAlert(withRouter(PermissionGroup)))} />
        </Route>

        <Route path="my-account/my-profile" element={React.createElement(withAlert(General))} />
        <Route path="my-account/my-profile/notifications" element={React.createElement(withAlert(MonitoringNotificationPreferencesPage))} />

        <Route path="trash" element={<LegacyDocumentsRouteGuard />}>
          <Route index element={React.createElement(withAlert(withRouter(TrashBin)))} />
        </Route>
        <Route path="document/filed" element={<LegacyDocumentsRouteGuard />}>
          <Route index element={React.createElement(withAlert(Filed))} />
        </Route>

        <Route path="backup" element={React.createElement(withAlert(withRouter(Backup)))} />

        <Route path="customer/update" element={React.createElement(withAlert(withRouter(Customer)))} />
        <Route path="document/configure" element={<LegacyDocumentsRouteGuard />}>
          <Route index element={React.createElement(withAlert(withRouter(ConfigHeaderFooter)))} />
        </Route>
        <Route path="terms" element={React.createElement(withAlert(AdhesionContract))} />

        <Route path="extract">
          <Route path=":nInvoiceId" element={React.createElement(withAlert(Extract))} />
          <Route path="history" element={React.createElement(withAlert(ExtractHistory))} />
        </Route>

        <Route path="operational-panel/migrate-responsibility/:nId" element={React.createElement(withAlert(MigrateResponsibilityOperational))} />
        <Route path="index" element={React.createElement(withAlert(Index))} />
        <Route path="register/activate/:cToken" element={<RegisterActivation />} />
        <Route path="document-share/:hash" element={<DocumentAccess />} />

        {/* Rota 404 para as que não baterem */}
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
};

export default RouterMain;
